import { Alert, AlertDescription, AlertIcon } from "@chakra-ui/alert";
import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { EqualizationPreviewTable } from "features/equalization/EqualizationPreviewTable";
import { useAllocationRuleGraphForm } from "features/fund-family/setup/fund-structure/hooks/useAllocationRuleGraphForm";
import { useAllocationRuleGraphAndUseRetrieveSuspense } from "gen/endpoints/allocation-rule-graph/allocation-rule-graph";
import { useEqualizationRetrieveSuspense } from "gen/endpoints/equalization/equalization";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import {
  FormFooter,
  FormFooterActions,
  FormFooterBreadcrumb,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { WarningIcon } from "shared/icons/WarningIcon";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { useBoolean } from "usehooks-ts";

export const AllocationRuleGraphEditEqualizationPreview: React.FC = () => {
  const { t } = useTranslation(["common", "equalization"]);
  const navigate = useNavigate();
  const {
    ruleGraphId,
    useId,
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
  } = useRequiredStringParams();

  const { data: equalizationBreakdownTableData } =
    useEqualizationRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
    );

  const { data: currentData } = useAllocationRuleGraphAndUseRetrieveSuspense(
    fundFamilyId,
    ruleGraphId,
    useId,
  );

  const navigateBackToDetailsTab = () => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fundStructure({})
        .fundFamilySetupAllocationRuleGraphAndUse({
          allocationRuleGraphId: ruleGraphId,
          allocationRuleGraphUseId: useId,
        }).$,
    );
  };

  const { onFinalize, onCancel } = useAllocationRuleGraphForm(
    fundFamilyId,
    true,
    {
      name: currentData.name,
      transactionCodeId: currentData.transaction_code_id ?? undefined,
      investmentId: currentData.investment_id ?? undefined,
      calculationId: currentData.calculation_id,
      edgeOverrideAmounts:
        currentData.security_ownership_allocation_overrides.map((override) => ({
          securityOwnershipId: override.security_ownership_id!,
          overrideAmount: override.override_amount,
        })),
    },
    {
      graphId: currentData.allocation_rule_graph_id,
      graphVersionId: currentData.latest_version_id,
      graphUseId: currentData.allocation_rule_graph_use_id,
      effectiveDate: currentData.effective_date,
      graphfundFamilyEventId: fundFamilyEventId,
      useFundFamilyEventId: undefined,
    },
    navigateBackToDetailsTab,
  );

  const onFinalizeEdits = async () => {
    await onFinalize();
    navigateBackToDetailsTab();
  };

  const {
    value: isWarningModalOpen,
    setFalse: onWarningModalClose,
    setTrue: onWarningModalOpen,
  } = useBoolean(false);

  const onBackToEdit = () => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fundStructure({})
        .fundFamilySetupAllocationRuleGraphEdit({
          ruleGraphId,
          useId,
        }).$,
    );
  };

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("allocation:equalizationPreview.title")}
        workflowName={t("allocation:ruleGraphEdit.workflowName")}
      />
      <Modal
        isOpen={isWarningModalOpen}
        onClose={onWarningModalClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("allocation:equalizationPreview.warningModalTitle")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Alert
              status="warning"
              mb={4}
              data-ls-enabled={true}
              data-public={true}
            >
              <AlertIcon>
                <WarningIcon />
              </AlertIcon>
              <AlertDescription>
                {t("allocation:equalizationPreview.warningModalBanner")}
              </AlertDescription>
            </Alert>
            <Text>
              {" "}
              {t("allocation:equalizationPreview.warningModalText", {
                date: formatIsoDateToDisplay(currentData.effective_date),
              })}
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={
                <Button onClick={onWarningModalClose}>
                  {t("allocation:equalizationPreview.back")}
                </Button>
              }
              confirm={
                <Button onClick={onFinalizeEdits}>
                  {t("allocation:equalizationPreview.finalize")}
                </Button>
              }
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Alert status="warning" mb={4} data-ls-enabled={true} data-public={true}>
        <AlertIcon>
          <WarningIcon />
        </AlertIcon>
        <AlertDescription>
          {t("allocation:equalizationPreview.warningBanner", {
            date: formatIsoDateToDisplay(currentData.effective_date),
          })}
        </AlertDescription>
      </Alert>
      <PageBody>
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:equalizationPreview")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <EqualizationPreviewTable
              tableData={equalizationBreakdownTableData}
            />
          </TableBoxBody>
        </TableBox>
        <FormFooter>
          <FormFooterBreadcrumb>
            <Breadcrumb
              separator={<ChevronRightIcon />}
              data-ls-enabled={true}
              data-public={true}
            >
              {[
                t("allocation:ruleGraphEdit.editDetailsBreadcrumb"),
                t("allocation:ruleGraphEdit.equalizationPreviewBreadcrumb"),
              ].map((title, index) => (
                <BreadcrumbItem key={index} isCurrentPage={index === 0}>
                  <BreadcrumbLink aria-disabled={true}>{title}</BreadcrumbLink>
                </BreadcrumbItem>
              ))}
            </Breadcrumb>
          </FormFooterBreadcrumb>
          <FormFooterActions>
            <FormCancelButton onClick={onBackToEdit} />
            <Button onClick={onCancel}>
              {t("allocation:equalizationPreview.exit")}
            </Button>
            <Button onClick={onWarningModalOpen}>
              {t("allocation:equalizationPreview.finalize")}
            </Button>
          </FormFooterActions>
        </FormFooter>
      </PageBody>
    </PageContent>
  );
};
